/**
 * Material Library — org-wide catalogue of reusable material
 * entries. Matches the design folder's `materials.html` 1:1.
 *
 * Layout:
 *   .projects-header — H1 "Materials Library" + count stats + + Add
 *   .filter-bar      — All / per-category chips + topbar search
 *   .material-grid   — 4-col responsive cards
 *
 * Behaviour:
 *   - Live read via `useMaterialLibrary(orgId)` (org-wide live snapshot)
 *   - Category chip filters client-side
 *   - Topbar search (`useTopbarSearch`) filters client-side
 *   - Click card → opens `MaterialLibraryFormSlideOver` in edit mode
 *   - `+ Add Material` opens it in create mode
 */
import { useEffect, useMemo, useState } from 'react';

import { canManageProjects } from '@shared/perms';

import { MaterialLibraryFormSlideOver } from '@/components/MaterialLibraryFormSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import {
  MATERIAL_CATEGORIES,
  getCategoryConfig,
  type MaterialCategory,
  type MaterialLibraryItem,
} from '@/lib/materialLibrary';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useMaterialLibrary } from '@/lib/useMaterialLibrary';

export function MaterialLibraryPage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const [localSearch, setLocalSearch] = useState('');
  const [category, setCategory] = useState<MaterialCategory | null>(null);
  const [formMode, setFormMode] = useState<
    { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; item: MaterialLibraryItem }
  >({ kind: 'closed' });
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canManageProjects(orgRole);

  useEffect(() => {
    setPlaceholder('Search materials by name, brand, variety…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const effectiveSearch = (searchQuery || localSearch).trim();
  const { data, loading } = useMaterialLibrary(orgId, effectiveSearch, category);

  // Stats: total items + unique categories represented.
  const stats = useMemo(() => {
    const total = data.length;
    const cats = new Set<string>();
    for (const r of data) cats.add(r.category);
    return { total, categoryCount: cats.size };
  }, [data]);

  return (
    <div className="page active" id="page-materials" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Materials Library</h1>
          <div className="projects-stats">
            {stats.total} item{stats.total === 1 ? '' : 's'} ·{' '}
            {stats.categoryCount} categor
            {stats.categoryCount === 1 ? 'y' : 'ies'}
          </div>
        </div>
        {canWrite ? (
          // Hidden for roles that can't write — Firestore rule blocks it anyway
          <button
            type="button"
            className="btn-primary"
            onClick={() => setFormMode({ kind: 'create' })}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Add Material
          </button>
        ) : null}
      </div>

      {/* Filter bar — All + per-category chips + local search */}
      <div className="filter-bar">
        <button
          type="button"
          className={`filter-chip${category === null ? ' active' : ''}`}
          onClick={() => setCategory(null)}
        >
          All
        </button>
        {MATERIAL_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${category === c.key ? ' active' : ''}`}
            onClick={() => setCategory(c.key)}
            title={c.label}
          >
            <i className={c.icon} /> {c.label}
          </button>
        ))}
        <div className="filter-sep" />
        <div className="filter-search-wrap" style={{ flex: 1 }}>
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="search"
            placeholder="Search materials…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {/* Grid / empty / loading */}
      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading library…
        </div>
      ) : data.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
            <i className="ph ph-package" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {effectiveSearch || category
              ? 'No materials match this filter'
              : 'Your library is empty'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 400,
              margin: '0 auto 16px',
            }}
          >
            {effectiveSearch || category
              ? 'Try a different search term or clear the category filter.'
              : 'Add reusable material entries here — they show up as picker options when you create material requests on a project.'}
          </div>
          {!effectiveSearch && !category && canWrite ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" /> Add your first material
            </button>
          ) : null}
        </div>
      ) : (
        <div className="material-grid">
          {data.map((m) => (
            <MaterialCard
              key={m.id}
              item={m}
              onClick={
                canWrite
                  ? () => setFormMode({ kind: 'edit', item: m })
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <MaterialLibraryFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        item={formMode.kind === 'edit' ? formMode.item : null}
      />
    </div>
  );
}

// ───── Card ─────

function MaterialCard({
  item,
  onClick,
}: {
  item: MaterialLibraryItem;
  /** Undefined → read-only card (no edit affordance). */
  onClick?: () => void;
}) {
  const cfg = getCategoryConfig(item.category);
  return (
    <div
      className="material-card"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && e.key === 'Enter') onClick();
      }}
      style={{ cursor: onClick ? 'pointer' : 'default', position: 'relative' }}
      title={onClick ? `Edit ${item.name}` : item.name}
    >
      {/* Category pill — pinned to the top-right corner over the image */}
      <span
        className="stage-pill"
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          padding: '3px 8px',
          fontSize: 10,
          background: cfg.tintBg,
          color: cfg.color,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <i className={cfg.icon} /> {cfg.label}
      </span>

      <div
        className="material-img"
        style={{
          background: cfg.tintBg,
          color: cfg.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
        }}
      >
        <i className={cfg.icon} />
      </div>
      <div className="material-info">
        <div className="material-brand">{item.brand || cfg.label}</div>
        <div className="material-title">{item.name}</div>
        {typeof item.defaultRate === 'number' && item.defaultRate > 0 ? (
          <div className="material-price">
            ₹{item.defaultRate.toLocaleString('en-IN')}{' '}
            <span className="material-unit">/ {item.unit}</span>
          </div>
        ) : (
          <div
            className="material-price"
            style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}
          >
            <span style={{ fontSize: 11 }}>No default rate</span>{' '}
            <span className="material-unit">/ {item.unit}</span>
          </div>
        )}
        <div
          className="material-meta"
          style={{ justifyContent: 'flex-start' }}
        >
          <span className="material-sku">
            {[item.variety, item.make, item.size].filter(Boolean).join(' · ') ||
              '—'}
          </span>
        </div>
      </div>
    </div>
  );
}
